import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';

import { scanCreateRequest } from '../../../../shared/constants/scan-create-request';
import { CustomValidators } from '../../services/helpers/form-custom-validators';
import { $scanHeaders } from '../../../../shared/constants/scan';
import { IScanHeader } from '../../../../shared/interfaces/scan.interface';

enum $fields {
  modifyHeaders = 'modifyHeaders',
}

@Component({
  selector: 'app-scan-extra-headers',
  templateUrl: './scan-extra-headers.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScanExtraHeadersComponent implements OnChanges {
  @Input()
  public form: UntypedFormGroup;
  @Input()
  public formValidationRequest$: Observable<void>;
  @Input()
  public headersControlName: string;
  @Input()
  modifyHeaders: boolean | null = null;

  public isDisabled: boolean;
  public isSelected: boolean;
  public $scanHeaders: typeof $scanHeaders;
  public shouldPersistHeaders: boolean = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.headersControlName = scanCreateRequest.headers;
    this.$scanHeaders = $scanHeaders;
  }

  @Input()
  public set disabled(value: boolean) {
    this.isDisabled = value;

    if (value) {
      this.headersForm?.disable({ emitEvent: false });
    } else {
      this.headersForm?.enable({ emitEvent: false });
    }

    this.changeDetectorRef.detectChanges();
  }

  public addControl(name: string = '', value: string = ''): void {
    this.headersForm.push(
      this.formBuilder.group({
        [$scanHeaders.name]: this.formBuilder.control(name, [CustomValidators.required, CustomValidators.validateIsEmpty]),
        [$scanHeaders.value]: this.formBuilder.control(value, [CustomValidators.required, CustomValidators.validateIsEmpty]),
      }),
    );

    this.changeDetectorRef.detectChanges();
  }

  @Input()
  public set headers(headers: IScanHeader[]) {
    if (Array.isArray(headers) && headers.length > 0) {
      if (this.form.contains(this.headersControlName) === false) {
        this.form.registerControl(this.headersControlName, this.formBuilder.array([]));
      } else {
        this.headersForm.clear();
      }

      this.isSelected = true;

      for (const header of headers) {
        this.addControl(header[$scanHeaders.name], header[$scanHeaders.value]);
      }
    } else {
      this.isSelected = false;
      this.onModifyHeaders(false);
    }
    this.changeDetectorRef.detectChanges();
  }

  public get headersForm(): UntypedFormArray {
    return this.form.get(this.headersControlName) as UntypedFormArray;
  }

  public get headersValues(): UntypedFormGroup[] {
    return this.headersForm.controls as UntypedFormGroup[];
  }

  public onModifyHeaders(value: boolean): void {
    if (this.shouldPersistHeaders) {
      this.form.get($fields.modifyHeaders)?.setValue(value);
      this.form.markAsDirty();
    }

    if (value === false) {
      this.form.removeControl(this.headersControlName);
      // Note: removing or adding controls doesn't mark the form as dirty automatically.
      // There's a mix of template driven forms (checkbox) with reactive forms.
      // Scheduling the markAsDirty call for the next change detection cycle
      // lets angular finish the current cycle first.
      // Otherwise, angular complains we are updating states in the middle of a change detection cycle.
      setTimeout(() => {
        this.form.markAsDirty();
        this.changeDetectorRef.detectChanges();
      });
      return;
    }

    if (this.form.contains(this.headersControlName) === false) {
      this.form.registerControl(this.headersControlName, this.formBuilder.array([]));
      this.addControl();
    }
  }

  public removeControl(index: number): void {
    this.headersForm.removeAt(index);
    // Note: removing or adding controls doesn't mark the form as dirty automatically.
    // removeControl runs before detection cycle begins, thus it doesn't interfere with angular lifecycle.
    this.form.markAsDirty();
    this.changeDetectorRef.detectChanges();
  }

  ngOnChanges(): void {
    // By default, the selection state follows the provided headers ( modifyHeaders = null )
    // The modifyHeaders parameter allows disabling this behavior even when headers are specified
    if (this.modifyHeaders !== null) {
      this.shouldPersistHeaders = true;
      this.isSelected = Boolean(this.modifyHeaders);
      this.form.registerControl($fields.modifyHeaders, this.formBuilder.control(this.isSelected));
    }
  }
}
